import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { TRANSPORTS_LIST, TRANSPORTS_REFETCH_LIST, TRANSPORT_SEARCH_HINTS } from 'api/query-keys.ts';
import type { TGetTransportsRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { ITransportFilter } from 'shared/stores/use-transport-filters';
import { setFiltersToQs } from 'shared/utils/filters/set-filters-to-qs';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query.ts';

interface IParams extends IWithPagination {
  isSkip?: boolean;
  isHints?: boolean;
  withRefetch?: boolean;
  search?: string;
  withNotUsed?: boolean;
  filters?: ITransportFilter;
}

export const useGetManyTransports = (params?: IParams) => {
  const {
    limit = PAGINATION_DEFAULT_LIMIT,
    page = 1,
    search,
    filters,
    isSkip = false,
    isHints = false,
    withRefetch = false,
    withNotUsed = false,
  } = params || {};

  const qs = new URLSearchParams({ limit: String(limit), page: String(page), withNotUsed: String(withNotUsed) });

  if (search) qs.set('filter[search]', search);
  if (filters) setFiltersToQs<ITransportFilter>(qs, filters);

  let queryKey = [TRANSPORTS_LIST, page, limit, search, filters];

  if (isHints) {
    queryKey = [TRANSPORT_SEARCH_HINTS, search];
  } else if (withRefetch) {
    queryKey = [TRANSPORTS_REFETCH_LIST, page, limit, search, filters];
  }

  return useQuery<WithPagination<TGetTransportsRes[]>>({
    queryKey,
    queryFn: async () => {
      const { data, headers } = await http.get<TGetTransportsRes[]>(`/v1/transports?${qs.toString()}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 60 * 1000,
    cacheTime: 60 * 1000,
    enabled: !isSkip,
    refetchInterval: withRefetch ? 2 * 60 * 1000 : false,
  });
};
